import React, { useState } from "react";
import { MediaProject } from "../../../../types/MediaProject";
import {
  publishTikTokCarousel,
  getTikTokPostStatus,
} from "../../../../database/community/tiktokConnection";
import { publishLinkedInPost } from "../../../../database/community/linkedinConnection";
import {
  publishToInstagram,
  publishToFacebookPage,
} from "../../../../database/community/metaConnection";
import { useAsyncAction } from "../hooks/useAsyncAction";
import PolicyBlockedNotice from "../sections/pipeline/PolicyBlockedNotice";
import { SmallButton, Muted } from "./shared";
import { createScheduledPost } from "../../../../database/community/scheduledPosts";

// Per-project publish actions, extracted from the connect-panel modules so
// connection UI and publish UI live apart. Consumed by the project cards and
// (later) the review queue.

const Column: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignItems: "flex-end",
};

// ─── TikTok ──────────────────────────────────────────────────────────────────

export const TikTokSendButton: React.FC<{
  communityId?: string;
  project: MediaProject;
  connected: boolean;
}> = ({ communityId, project, connected }) => {
  const action = useAsyncAction();
  const [status, setStatus] = useState(project.tiktokStatus || "");

  const isCarousel = project.kind === "carousel";
  const slideCount = (project.slides || []).length;
  const disabled =
    !communityId || !connected || !isCarousel || slideCount === 0 || action.busy;
  const disabledReason = !isCarousel
    ? "Only carousels post to TikTok"
    : !connected
    ? "Connect TikTok first"
    : slideCount === 0
    ? "This carousel has no slides"
    : "";

  const handleSend = () => {
    if (!communityId) return;
    action.run(
      async () => {
        const res = await publishTikTokCarousel({
          communityId,
          projectId: project.id,
        });
        if (!res.ok) {
          throw new Error("TikTok did not accept the post.");
        }
        setStatus("PROCESSING");
        return "Sent to your TikTok inbox — finish it in the TikTok app.";
      },
      { fallbackError: "Could not send to TikTok." }
    );
  };

  const handleCheck = () => {
    if (!communityId) return;
    action.run(
      async () => {
        const res = await getTikTokPostStatus({
          communityId,
          projectId: project.id,
        });
        setStatus(res.status);
      },
      { fallbackError: "Could not fetch status." }
    );
  };

  if (!isCarousel) return null;

  return (
    <div style={Column}>
      <div style={{ display: "flex", gap: 6 }}>
        {project.tiktokPublishId ? (
          <SmallButton
            type="button"
            onClick={handleCheck}
            disabled={action.busy || !communityId}
          >
            Status
          </SmallButton>
        ) : null}
        <SmallButton
          type="button"
          onClick={handleSend}
          disabled={disabled}
          title={disabledReason || undefined}
        >
          {action.busy ? "Sending…" : "Send to TikTok"}
        </SmallButton>
      </div>
      {status ? <Muted>TikTok: {status}</Muted> : null}
      {action.notice ? <Muted>{action.notice}</Muted> : null}
      {action.error ? (
        <Muted style={{ color: "var(--color-danger)" }}>{action.error}</Muted>
      ) : null}
      <PolicyBlockedNotice error={action.error} />
    </div>
  );
};

// ─── LinkedIn ────────────────────────────────────────────────────────────────

export const LinkedInPostButton: React.FC<{
  communityId?: string;
  project: MediaProject;
  connected: boolean;
}> = ({ communityId, project, connected }) => {
  const action = useAsyncAction();
  const [posted, setPosted] = useState(Boolean(project.linkedinPostUrn));

  const hasCaption = Boolean((project.caption || "").trim());
  const disabled = !communityId || !connected || !hasCaption || action.busy;
  const disabledReason = !connected
    ? "Connect LinkedIn first"
    : !hasCaption
    ? "This project has no caption to post"
    : "";

  const handlePost = () => {
    if (!communityId) return;
    action.run(
      async () => {
        const firstSlide = (project.slides || [])[0]?.mediaUrl;
        const res = await publishLinkedInPost({
          communityId,
          projectId: project.id,
          imageUrl: firstSlide,
        });
        if (!res.ok) {
          throw new Error("LinkedIn did not accept the post.");
        }
        setPosted(true);
        return "Posted to LinkedIn.";
      },
      { fallbackError: "Could not post to LinkedIn." }
    );
  };

  return (
    <div style={Column}>
      <SmallButton
        type="button"
        onClick={handlePost}
        disabled={disabled}
        title={disabledReason || undefined}
      >
        {action.busy
          ? "Posting…"
          : posted
          ? "Post again to LinkedIn"
          : "Post to LinkedIn"}
      </SmallButton>
      {action.notice ? <Muted>{action.notice}</Muted> : null}
      {action.error ? (
        <Muted style={{ color: "var(--color-danger)" }}>{action.error}</Muted>
      ) : null}
      <PolicyBlockedNotice error={action.error} />
    </div>
  );
};

// ─── Instagram / Facebook ────────────────────────────────────────────────────

export const MetaPublishButtons: React.FC<{
  communityId?: string;
  project: MediaProject;
  igConnected: boolean;
  pageConnected: boolean;
}> = ({ communityId, project, igConnected, pageConnected }) => {
  const action = useAsyncAction();
  const [busyTarget, setBusyTarget] = useState<"ig" | "fb" | "">("");

  const isCarousel = project.kind === "carousel";
  const firstImage = (project.slides || [])[0]?.mediaUrl;
  const reelUrl = project.outputUrl;

  const handleInstagram = () => {
    if (!communityId) return;
    setBusyTarget("ig");
    action
      .run(
        async () => {
          const res = await publishToInstagram({
            communityId,
            projectId: project.id,
          });
          if (!res.ok) throw new Error("Instagram did not accept the post.");
          return "Posted to Instagram.";
        },
        { fallbackError: "Could not post to Instagram." }
      )
      .finally(() => setBusyTarget(""));
  };

  const handleFacebook = () => {
    if (!communityId) return;
    setBusyTarget("fb");
    action
      .run(
        async () => {
          const res = await publishToFacebookPage({
            communityId,
            message: project.caption,
            imageUrl: isCarousel ? firstImage : undefined,
            videoUrl: !isCarousel ? reelUrl : undefined,
          });
          if (!res.ok) throw new Error("Facebook did not accept the post.");
          return "Posted to your Facebook Page.";
        },
        { fallbackError: "Could not post to Facebook." }
      )
      .finally(() => setBusyTarget(""));
  };

  const igDisabled = !communityId || !igConnected || action.busy;
  const fbDisabled = !communityId || !pageConnected || action.busy;

  return (
    <div style={Column}>
      <div style={{ display: "flex", gap: 6 }}>
        <SmallButton
          type="button"
          onClick={handleInstagram}
          disabled={igDisabled}
          title={!igConnected ? "Connect an IG Business account first" : undefined}
        >
          {busyTarget === "ig" ? "Posting…" : "Post to Instagram"}
        </SmallButton>
        <SmallButton
          type="button"
          onClick={handleFacebook}
          disabled={fbDisabled}
          title={!pageConnected ? "Connect a Facebook Page first" : undefined}
        >
          {busyTarget === "fb" ? "Posting…" : "Post to Facebook"}
        </SmallButton>
      </div>
      {project.igStatus ? <Muted>Instagram: {project.igStatus}</Muted> : null}
      {action.notice ? <Muted>{action.notice}</Muted> : null}
      {action.error ? (
        <Muted style={{ color: "var(--color-danger)" }}>{action.error}</Muted>
      ) : null}
      <PolicyBlockedNotice error={action.error} />
    </div>
  );
};

// ─── YouTube ────────────────────────────────────────────────────────────────

export const YouTubePostButton: React.FC<{
  communityId?: string;
  project: MediaProject;
  connected: boolean;
  channelLabel?: string;
}> = ({ communityId, project, connected, channelLabel }) => {
  const action = useAsyncAction();
  const [privacy, setPrivacy] = useState<"private" | "unlisted" | "public">(
    "private"
  );
  const [title, setTitle] = useState(
    project.title || project.caption?.split(/\r?\n/).find(Boolean) || "Tribed video"
  );
  const [description, setDescription] = useState(project.caption || "");
  if (project.kind !== "reel") return null;

  const disabled =
    !communityId ||
    !connected ||
    !project.outputUrl ||
    !title.trim() ||
    action.busy;
  const disabledReason = !connected
    ? "Connect YouTube first"
    : !project.outputUrl
      ? "Render this reel before uploading it"
      : !title.trim()
        ? "Add a YouTube title"
      : "";

  const handlePost = () => {
    if (!communityId) return;
    action.run(
      async () => {
        await createScheduledPost(communityId, {
          platforms: ["youtube"],
          caption: description,
          projectId: project.id,
          dueAt: new Date().toISOString(),
          via: "firstparty",
          platformParams: {
            youtube: {
              title: title.trim(),
              description,
              privacyStatus: privacy,
              notifySubscribers: false,
            },
          },
        });
        return "Queued for YouTube upload now.";
      },
      { fallbackError: "Could not queue the YouTube upload." }
    );
  };

  return (
    <div style={{ ...Column, alignItems: "stretch", width: "100%" }}>
      {connected ? (
        <Muted>
          Upload to YouTube{channelLabel ? ` · ${channelLabel}` : ""}
        </Muted>
      ) : null}
      <label>
        <Muted>YouTube title</Muted>
        <input
          aria-label="YouTube title"
          value={title}
          maxLength={100}
          onChange={(e) => setTitle(e.target.value)}
          disabled={action.busy}
          style={{ width: "100%" }}
        />
      </label>
      <label>
        <Muted>YouTube description</Muted>
        <textarea
          aria-label="YouTube description"
          value={description}
          maxLength={5000}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          disabled={action.busy}
          style={{ width: "100%", resize: "vertical" }}
        />
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        <select
          aria-label="YouTube visibility"
          value={privacy}
          onChange={(e) =>
            setPrivacy(e.target.value as "private" | "unlisted" | "public")
          }
          disabled={action.busy}
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            padding: "3px 6px",
            borderRadius: 999,
            border: "1px solid var(--color-border-subtle)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
          }}
        >
          <option value="private">Private</option>
          <option value="unlisted">Unlisted</option>
          <option value="public">Public</option>
        </select>
        <SmallButton
          type="button"
          onClick={handlePost}
          disabled={disabled}
          title={disabledReason || undefined}
        >
          {action.busy ? "Queuing…" : "Post to YouTube"}
        </SmallButton>
      </div>
      {project.youtubeStatus ? <Muted>YouTube: {project.youtubeStatus}</Muted> : null}
      {project.youtubeVideoId ? (
        <Muted>
          <a
            href={`https://www.youtube.com/watch?v=${encodeURIComponent(
              project.youtubeVideoId
            )}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: "inherit" }}
          >
            View video
          </a>
        </Muted>
      ) : null}
      {action.notice ? <Muted>{action.notice}</Muted> : null}
      {action.error ? (
        <Muted style={{ color: "var(--color-danger)" }}>{action.error}</Muted>
      ) : null}
      <PolicyBlockedNotice error={action.error} />
    </div>
  );
};
